<script setup>
import { computed, reactive, ref, watch } from "vue";
import { timezones } from "db";
import { trans } from "i18n";
import { getErrorMessage } from "lib/modules/util";
import Http from "lib/modules/Http";

const props = defineProps(["cachedEmail", "readonlyEmail"]);
const emit = defineEmits(["activeComponent", "login", "update:busy", "update:cachedEmail"]);

const http = reactive(new Http({}));
const data = reactive({
	email: props.cachedEmail,
	promo: false,
	lang_id: document.documentElement.lang,
	timezone_id: new Intl.DateTimeFormat().resolvedOptions().timeZone,
	persistent: true
});
const verifyPassword = ref("");
const accept = ref(false);

const passwordError = computed(() => {
	return data.password !== verifyPassword.value && verifyPassword.value.length > 0 ? trans.gettext("Password mismatch") : "";
});

function track(action) {
	$vixAnalytics({
		ca: 1,
		e_c: "Registration",
		e_a: "Registration " + action
	});
}

function register() {
	http.post("/api/register", data, () => {
		if (http.data.reg_token) {
			data.token = http.data.reg_token;

			track("Submit");
		}
		else {
			$vixAnalytics({ idgoal: 4 });

			emit("login", data.email);
		}
	},
	() => {
		if (http.error === "token expired") {
			delete data.key;
			delete data.token;
			http.error = trans.gettext("The verification code has expired, click '%1' to get a new code.", trans.gettext("Register"));
		}
		else if (http.error === `duplicate entry: ${data.email}`)
			http.error = trans.gettext("This e-mail address is already registered. Please login or reset your password.");
	});
}

watch(() => http.busy, (val) => emit("update:busy", val));

track("Start");
</script>

<template>
	<form @submit.prevent="register()">
		<h2>{{ $gettext("Register") }}</h2>
		<fieldset :disabled="http.busy" class="grid gap-8">
			<div v-if="!data.token" class="grid gap-16">
				<UiInput
					v-model="data.fname"
					v-focus
					:label="$gettext('First Name')"
					autocomplete="given-name"
					type="text"
					maxlength="64"
					required
				/>
				<UiInput
					v-model="data.lname"
					:label="$gettext('Last Name')"
					autocomplete="family-name"
					type="text"
					maxlength="64"
					required
				/>
				<UiInput
					v-model="data.email"
					:label="$gettext('E-mail')"
					:readonly="readonlyEmail"
					autocomplete="email"
					placeholder="example@email.com"
					type="email"
					maxlength="128"
					required
					@update:model-value="$emit('update:cachedEmail', email)"
				/>
				<div class="grid gap-16">
					<UiInput
						:value="data.password"
						:label=" $gettext('Password')"
						:placeholder="$gettext('Password')"
						autocomplete="new-password"
						minlength="8"
						type="password"
						required
						@input="data.password = $event.target.value"
					/>
					<UiInput
						:value="verifyPassword"
						:error="passwordError"
						:placeholder="$gettext('Verify password')"
						autocomplete="new-password"
						type="password"
						required
						@input="verifyPassword = $event.target.value"
					/>
				</div>
				<UiInput
					v-model="data.phone"
					:label="$gettext('Phone')"
					autocomplete="tel"
					placeholder="+1 23456789"
					type="tel"
					maxlength="32"
					required
				/>
				<UiSelect
					v-model="data.timezone_id"
					:label="$gettext('Time Zone')"
					:options="timezones"
					required
				/>
				<UiCheckbox
					v-model="data.promo"
					:label="$gettext('I accept to receive news from Vixly')"
				/>
				<UiCheckbox v-model="accept" required>
					<span>{{ $gettext("I accept") }} <a href="legal/terms" target="_blank">{{ $gettext("Vixly Terms of Service") }}</a></span>
				</UiCheckbox>
			</div>
			<div v-else class="grid gap-16">
				<p>{{ $gettext("A verification code has been sent to your e-mail address %1. Please enter this code below.", data.email) }}</p>
				<UiInput
					v-model="data.key"
					:label="$gettext('Verification code')"
					autocomplete="one-time-code"
					type="text"
					required
				/>
			</div>
			<div class="vui-error justify-self-end">{{ getErrorMessage(http.error) }}</div>
			<div class="grid cols-2 gap-16">
				<button type="button" class="vui-btn outlined icon-left justify-self-start" @click="$emit('activeComponent', 'Login')">
					<i class="fa-regular fa-arrow-left"></i>
					<span>{{ $gettext("Login") }}</span>
				</button>
				<button type="submit" class="vui-btn justify-self-end">{{ $gettext("Register") }}</button>
			</div>
		</fieldset>
	</form>
</template>
